<template>
  <div>
    <div class="row">
      <div class="col-lg-12 col-xxl-12">
        <div class="card card-custom gutter-b">
          <div class="card-header border-0 py-5">
            <h3 class="card-title align-items-start flex-column">
              <span class="card-label font-weight-bolder text-dark">
                ระดับสมาชิก
              </span>
              <span class="text-muted mt-1 font-weight-bold font-size-sm">
                จัดการระดับสมาชิก เกณฑ์คะแนน และสิทธิประโยชน์
              </span>
            </h3>
            <div class="card-toolbar">
              <b-button
                v-if="canCreateTier"
                variant="primary"
                class="font-weight-bolder"
                style="padding-top: 8px; padding-bottom: 8px"
                @click="$router.push(createTierRoute)"
              >
                เพิ่มระดับสมาชิก
              </b-button>
            </div>
          </div>

          <div class="card-body pt-0 pb-3">
            <div class="table-responsive">
              <b-table
                id="member-tier-table"
                :items="items"
                :fields="fields"
                :busy="isLoadingTiers"
                :borderless="true"
                head-variant="light"
                hover
                show-empty
                empty-text="ไม่พบข้อมูลระดับสมาชิก"
              >
                <template #table-busy>
                  <div class="text-center text-muted my-3">
                    <b-spinner small class="mr-2"></b-spinner>
                    กำลังโหลดข้อมูล...
                  </div>
                </template>

                <template #cell(index)="data">
                  <span class="text-muted font-weight-bold">
                    {{ data.index + 1 }}
                  </span>
                </template>

                <template #cell(image)="data">
                  <div
                    class="d-flex align-items-center justify-content-center"
                    :style="{
                      backgroundColor: data.item.image_url
                        ? 'transparent'
                        : (data.item.color || '#3699FF') + '20',
                      width: '48px',
                      height: '48px'
                    }"
                  >
                    <img
                      v-if="data.item.image_url"
                      :src="data.item.image_url"
                      style="width: 100%; height: 100%; object-fit: contain"
                    />
                    <span
                      v-else
                      class="svg-icon svg-icon-md"
                      :style="{ color: data.item.color || '#3699FF' }"
                    >
                      <inline-svg src="media/svg/icons/General/Star.svg" />
                    </span>
                  </div>
                </template>

                <template #cell(name)="data">
                  <div style="max-width: 320px">
                    <span
                      class="font-weight-bolder text-dark font-size-lg d-block"
                    >
                      {{ data.item.name }}
                    </span>
                    <span
                      class="text-muted font-size-sm d-block text-truncate"
                    >
                      {{ benefitSummary(data.item.benefits) }}
                    </span>
                  </div>
                </template>

                <template #cell(min_points)="data">
                  <span class="font-weight-bold text-dark">
                    {{ Number(data.item.min_points).toLocaleString() }} แต้ม
                  </span>
                </template>

                <template #cell(max_points)="data">
                  <span
                    v-if="
                      data.item.max_points !== null &&
                      data.item.max_points !== undefined
                    "
                    class="font-weight-bold text-dark"
                  >
                    {{ Number(data.item.max_points).toLocaleString() }} แต้ม
                  </span>
                  <span v-else class="text-muted">
                    ไม่จำกัด
                  </span>
                </template>

                <template #cell(multiplier)="data">
                  <span
                    class="badge badge-light-primary font-weight-bold px-3 py-2"
                  >
                    x{{ data.item.multiplier || 1 }} เท่า
                  </span>
                </template>


                <template #cell(actions)="data">
                  <div class="d-flex justify-content-center">
                    <a
                      href="javascript:"
                      class="btn btn-icon btn-light btn-sm"
                      @click.prevent="
                        $router.push(editTierRoute(data.item.id))
                      "
                    >
                      <span class="svg-icon svg-icon-md svg-icon-success">
                        <inline-svg src="media/svg/icons/General/Edit.svg" />
                      </span>
                    </a>
                  </div>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-4">
      <div class="col-lg-12 col-xxl-12">
        <div class="card card-custom gutter-b">
          <div class="card-header border-0 py-5">
            <h3 class="card-title align-items-start flex-column">
              <span class="card-label font-weight-bolder text-dark">
                รายชื่อสมาชิก
              </span>
              <span class="text-muted mt-1 font-weight-bold font-size-sm">
                ตรวจสอบรายชื่อสมาชิก ระดับสมาชิกปัจจุบัน และคะแนนสะสม
              </span>
            </h3>
            <div class="card-toolbar d-flex align-items-center">
              <b-form-select
                v-model="selectedTierFilter"
                :options="tierFilterOptions"
                class="mr-3 form-control form-control-solid"
                style="width: 170px"
              ></b-form-select>

              <div class="input-icon input-icon-right" style="width: 220px">
                <b-form-input
                  v-model="searchQuery"
                  debounce="400"
                  class="form-control-solid"
                  :placeholder="
                    $t('PAGE.DEFAULT.TABLE.SEARCH') ||
                    'ค้นหาชื่อ หรือ เบอร์โทร...'
                  "
                  @update="onSearchChange"
                ></b-form-input>
                <span>
                  <span class="svg-icon svg-icon-sm svg-icon-muted">
                    <inline-svg src="media/svg/icons/General/Search.svg" />
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div class="card-body pt-0 pb-5">
            <div class="table-responsive">
              <b-table
                id="members-table"
                :items="memberItems"
                :fields="memberFields"
                :busy="isLoadingMembers"
                :borderless="true"
                head-variant="light"
                hover
                show-empty
                empty-text="ไม่พบข้อมูลสมาชิก"
              >
                <template #table-busy>
                  <div class="text-center text-muted my-3">
                    <b-spinner small class="mr-2"></b-spinner>
                    กำลังโหลดข้อมูล...
                  </div>
                </template>

                <template #cell(index)="data">
                  <span class="text-muted font-weight-bold">
                    {{ (currentPage - 1) * perPage + data.index + 1 }}
                  </span>
                </template>

                <template #cell(member)="data">
                  <div class="d-flex align-items-center">
                    <div
                      class="symbol symbol-40 symbol-circle mr-3"
                      style="overflow: hidden; background-color: #e4e6ef; flex-shrink: 0;"
                    >
                      <img
                        :src="getMemberAvatarSrc(data.item)"
                        referrerpolicy="no-referrer"
                        style="width: 40px; height: 40px; object-fit: cover"
                        @error="onAvatarError(data.item, $event)"
                      />
                    </div>
                    <div>
                      <span class="font-weight-bolder text-dark d-block">
                        {{ data.item.name }}
                      </span>
                      <span class="text-muted font-size-xs">
                        LINE: {{ data.item.line_id || "-" }}
                      </span>
                    </div>
                  </div>
                </template>

                <template #cell(phone)="data">
                  <span class="font-weight-bold text-dark">
                    {{ data.item.phone || "-" }}
                  </span>
                </template>

                <template #cell(tier)="data">
                  <span
                    v-if="data.item.tier_name"
                    class="badge font-weight-bold px-3 py-2"
                    :style="{
                      backgroundColor:
                        (data.item.tier_color || '#3699FF') + '20',
                      color: data.item.tier_color || '#3699FF',
                      border: `1px solid ${data.item.tier_color || '#3699FF'}40`
                    }"
                  >
                    {{ data.item.tier_name }}
                  </span>
                  <span
                    v-else
                    class="badge badge-light-dark font-weight-bold px-3 py-2"
                    style="color: #999"
                  >
                    ไม่มีระดับ
                  </span>
                </template>

                <template #cell(points)="data">
                  <span class="font-weight-bolder text-primary font-size-lg">
                    {{ Number(data.item.points || 0).toLocaleString() }}
                  </span>
                  <span class="text-muted font-size-xs ml-1">แต้ม</span>
                </template>

                <template #cell(created_at)="data">
                  <span class="text-muted font-size-sm">
                    {{ formatDate(data.item.created_at) }}
                  </span>
                </template>

                <template #cell(actions)="data">
                  <div class="d-flex justify-content-center">
                    <b-button
                      size="sm"
                      variant="outline-primary"
                      :disabled="
                        !data.item.campaign_id ||
                        !data.item.customer_campaign_id
                      "
                      @click="viewMemberDetail(data.item)"
                    >
                      {{
                        $t(
                          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.MEMBERS.ACTION_VIEW_DETAIL"
                        ) || "ดูรายละเอียด"
                      }}
                    </b-button>
                  </div>
                </template>
              </b-table>
            </div>

            <div
              class="d-flex justify-content-between align-items-center flex-wrap mt-3"
            >
              <span class="text-muted font-size-sm">
                แสดง {{ paginationFrom }} ถึง {{ paginationTo }} จากทั้งหมด
                {{ totalMembers }} รายการ
              </span>
              <b-pagination
                v-model="currentPage"
                :total-rows="totalMembers"
                :per-page="perPage"
                align="right"
                class="my-0"
                @change="onPageChange"
              ></b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ApiService from "@/core/services/api.service";
import { Config } from "@/core/config/variable.config";

export default {
  name: "MemberTier",
  props: {
    companyId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      fields: [
        {
          key: "index",
          label: "#",
          class: "text-center align-middle",
          thStyle: { width: "60px" }
        },
        {
          key: "image",
          label: "ตราสัญลักษณ์",
          class: "text-center align-middle",
          thStyle: { width: "100px" }
        },
        { key: "name", label: "ชื่อระดับสมาชิก", class: "align-middle" },
        {
          key: "min_points",
          label: "เกณฑ์คะแนนขั้นต่ำ",
          class: "align-middle"
        },
        { key: "max_points", label: "เกณฑ์คะแนนสูงสุด", class: "align-middle" },
        { key: "multiplier", label: "ตัวคูณแต้มสะสม", class: "align-middle" },
        {
          key: "actions",
          label: "จัดการ",
          class: "text-center align-middle",
          thStyle: { width: "80px" }
        }
      ],
      items: [],
      canCreateTier: false,
      isLoadingTiers: false,
      memberFields: [
        {
          key: "index",
          label: "#",
          class: "text-center align-middle",
          thStyle: { width: "60px" }
        },
        { key: "member", label: "สมาชิก", class: "align-middle" },
        { key: "phone", label: "เบอร์โทรศัพท์", class: "align-middle" },
        { key: "tier", label: "ระดับสมาชิก", class: "align-middle" },
        { key: "points", label: "คะแนนสะสม", class: "align-middle" },
        { key: "created_at", label: "วันที่สมัคร", class: "align-middle" },
        {
          key: "actions",
          label: "จัดการ",
          class: "text-center align-middle",
          thStyle: { width: "140px" }
        }
      ],
      searchQuery: "",
      selectedTierFilter: "all",
      currentPage: 1,
      perPage: 10,
      totalMembers: 0,
      isLoadingMembers: false,
      memberItems: []
    };
  },
  computed: {
    effectiveCompanyId() {
      return (
        this.companyId ||
        this.$route.params.companyId ||
        this.$route.query.companyId ||
        null
      );
    },
    createTierRoute() {
      return this.effectiveCompanyId
        ? `/company-management/${this.effectiveCompanyId}/member-tier/form`
        : "/member-tier/form";
    },
    tierFilterOptions() {
      const options = [{ value: "all", text: "ทุกระดับสมาชิก" }];
      this.items.forEach((tier) => {
        options.push({ value: tier.id, text: `ระดับ ${tier.name}` });
      });
      return options;
    },
    paginationFrom() {
      if (this.totalMembers === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationTo() {
      const to = this.currentPage * this.perPage;
      return to > this.totalMembers ? this.totalMembers : to;
    },
    defaultAvatar() {
      return `${process.env.BASE_URL}media/users/default.jpg`;
    }
  },
  watch: {
    selectedTierFilter() {
      this.currentPage = 1;
      this.loadMembers();
    },
    $route: {
      handler(to, from) {
        if (!from) return;
        const companyParamChanged =
          to.params.companyId !== from.params.companyId;
        const companyQueryChanged =
          to.query.companyId !== from.query.companyId;
        if (companyParamChanged || companyQueryChanged) {
          const companyRoute = this.effectiveCompanyId
            ? "/company-management"
            : "/company";
          this.$store.dispatch(SET_BREADCRUMB, [
            {
              title: `${this.$t("MENU.COMPANY_MANAGEMENT.TITLE")}`,
              route: companyRoute
            },
            { title: "ระดับสมาชิก" }
          ]);
          this.currentPage = 1;
          this.loadMemberTiers();
          this.loadMembers();
        }
      }
    },
    companyId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentPage = 1;
        this.loadMemberTiers();
        this.loadMembers();
      }
    }
  },
  mounted() {
    const companyRoute = this.effectiveCompanyId
      ? "/company-management"
      : "/company";
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: `${this.$t("MENU.COMPANY_MANAGEMENT.TITLE")}`,
        route: companyRoute
      },
      { title: "ระดับสมาชิก" }
    ]);

    this.loadMemberTiers();
    this.loadMembers();
  },
  methods: {
    editTierRoute(id) {
      return this.effectiveCompanyId
        ? `/company-management/${this.effectiveCompanyId}/member-tier/form/${id}`
        : `/member-tier/form/${id}`;
    },
    benefitSummary(benefits) {
      const html = (benefits || [])
        .map((b) => `<p>${b.description || ""}</p>`)
        .join("")
        .replace(/<\/(p|li|h[1-6]|blockquote|td|th|pre)>|<br\s*\/?>/gi, "$&\n");
      const text = new DOMParser().parseFromString(html, "text/html").body
        .textContent;
      const parts = text
        .split("\n")
        .map((part) => part.trim())
        .filter((part) => part !== "");
      if (parts.length) {
        return parts.join(", ");
      }
      return /<(img|figure|oembed)[\s>/]/i.test(html) ? "(รูปภาพ)" : "-";
    },
    onSearchChange() {
      this.currentPage = 1;
      this.loadMembers();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.$nextTick(() => {
        this.loadMembers();
      });
    },
    loadMemberTiers() {
      this.isLoadingTiers = true;
      ApiService.setHeader();
      const params = {};
      if (this.effectiveCompanyId) {
        params.company_id = this.effectiveCompanyId;
      }
      ApiService.getParams("member_tier", Object.keys(params).length ? { params } : undefined)
        .then(({ data }) => {
          const items = data && data.data ? data.data.items : null;
          if (data && data.status === "success" && Array.isArray(items)) {
            this.items = items.map(tier => ({
              id: tier.id,
              name: tier.name,
              image_url: tier.image
                ? `${Config.ADMIN_IMAGE_URL}/${tier.image}`
                : null,
              min_points: tier.min_point,
              max_points: tier.max_point,
              multiplier: tier.point_multiplier,
              color: tier.color,
              benefits: Array.isArray(tier.benefits) ? tier.benefits : []
            }));
            this.canCreateTier = data.data.can_create !== false;
          }
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.isLoadingTiers = false;
        });
    },
    loadMembers() {
      this.isLoadingMembers = true;
      ApiService.setHeader();
      const params = {
        page: this.currentPage,
        per_page: this.perPage
      };
      if (this.effectiveCompanyId) {
        params.company_id = this.effectiveCompanyId;
      }
      if (this.selectedTierFilter && this.selectedTierFilter !== "all") {
        params.tier_id = this.selectedTierFilter;
      }
      if (this.searchQuery && this.searchQuery.trim()) {
        params.q = this.searchQuery.trim();
      }

      ApiService.getParams("member_tier/members", { params })
        .then(({ data }) => {
          if (data && data.status === "success" && data.data) {
            this.memberItems = Array.isArray(data.data.data)
              ? data.data.data.map((item) => ({ ...item, avatarError: false }))
              : [];
            this.totalMembers = Number(data.data.total) || 0;
          } else {
            this.memberItems = [];
            this.totalMembers = 0;
          }
        })
        .catch(() => {
          this.memberItems = [];
          this.totalMembers = 0;
        })
        .finally(() => {
          this.isLoadingMembers = false;
        });
    },
    formatDate(v) {
      if (!v) return "-";
      try {
        const d = new Date(v);
        if (isNaN(d.getTime())) return v;
        return d.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "short",
          day: "numeric"
        });
      } catch (e) {
        return v;
      }
    },
    viewMemberDetail(member) {
      const campaignId = member.campaign_id;
      const customerCampaignId = member.customer_campaign_id;
      if (campaignId && customerCampaignId) {
        this.$router.push(
          `/campaign/form/campaign_receipt/${campaignId}/member/${customerCampaignId}`
        );
      }
    },
    getAvatarUrl(avatar) {
      if (!avatar || typeof avatar !== "string") return null;
      const s = avatar.trim();
      if (
        !s ||
        s === "undefined" ||
        s === "null" ||
        s.includes("default_profile") ||
        s.includes("Ellipse 36")
      ) {
        return null;
      }
      if (/^https?:\/\//i.test(s)) {
        return s;
      }
      return `${Config.ADMIN_IMAGE_URL}/${s.replace(/^\//, "")}`;
    },
    getMemberAvatarSrc(item) {
      if (!item || item.avatarError) {
        return this.defaultAvatar;
      }
      const url = this.getAvatarUrl(item.avatar);
      return url || this.defaultAvatar;
    },
    onAvatarError(item, event) {
      if (item && !item.avatarError) {
        this.$set(item, "avatarError", true);
      }
      if (event && event.target && event.target.src !== this.defaultAvatar) {
        event.target.src = this.defaultAvatar;
      }
    }
  }
};
</script>

<style scoped>
* {
  font-family: SukhumvitSet, sans-serif !important;
}
</style>
